/**
 * WarpGeometryPassiveSkillEffect — Astral rift-path passive: arrival knockback
 * near active Anchor wells.
 *
 * When unlocked, registers a listener on AstralTeleportSystem.onTeleport.
 * On each teleport event, checks if either the departure or arrival point lies
 * within the pull radius of any active Anchor well. If proximity is detected,
 * applies a radial knockback pulse at the arrival point — pushing all enemies
 * within arrivalKnockbackRadiusTiles (0.5 tiles) away. Pure positional
 * displacement, no damage.
 *
 * The "within the pull radius" check converts pullRadiusTiles to pixels
 * (pullRadiusTiles × TILE_SIZE) before comparing against pixel-space positions.
 *
 * Apotheosis parent: one of the two OR-gate parents for
 * apotheosis_event_horizon (Dead Star). A player who walks the Anchor sub-path
 * unlocks this and Dead Star becomes reachable without event_horizon_passive.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § anchor_passive
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { TeleportEvent } from '../../../systems/AstralTeleportSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WarpGeometryAnchorWell {
  position: { x: number; y: number };
  pullRadiusTiles: number;
}

export interface WarpGeometryDeps {
  /** AstralTeleportSystem-compatible source for teleport events. */
  readonly teleport: {
    onTeleport(callback: (event: TeleportEvent) => void): void;
  };
  /**
   * Provides the current set of active Anchor wells with their pull radii.
   * Backed by AnchorSkillEffect.getActiveWells() in game integration.
   */
  readonly anchorSource: {
    getActiveWells(): ReadonlyArray<WarpGeometryAnchorWell>;
  };
  /** Returns IDs of enemies within a circular radius (in tiles) of a point. */
  readonly enemies: {
    getEnemiesInRadius(center: { x: number; y: number }, radiusTiles: number): string[];
  };
  /**
   * Applies a positional push to a single enemy, directed away from the
   * given center point. No damage — pure displacement.
   */
  readonly knockback: {
    pushFromPoint(enemyId: string, center: { x: number; y: number }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WarpGeometryConfig {
  /** Knockback pulse radius in tiles centered on the teleport arrival point. */
  readonly arrivalKnockbackRadiusTiles: number;
}

const DEFAULT_CONFIG: WarpGeometryConfig = {
  arrivalKnockbackRadiusTiles: 0.5,
};

import { TILE_SIZE } from '../../../utils/Constants.js';


// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WarpGeometryPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'anchor_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: WarpGeometryDeps;
  readonly config: WarpGeometryConfig;

  /**
   * Number of teleport events that triggered a proximity pulse.
   * Increments once per qualifying teleport, regardless of how many enemies
   * were in the knockback radius. Useful for asserting "no pulse fired" (=0)
   * vs "pulse fired" (>0) in simulation scenarios.
   */
  private _knockbackPulseCount = 0;
  /** Total enemies knocked back across all pulses. */
  private _enemiesKnockedBack = 0;

  constructor(deps: WarpGeometryDeps, config: Partial<WarpGeometryConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Register at unlock time. AstralTeleportSystem has no unregister API —
    // the listener remains for the lifetime of the tree session.
    deps.teleport.onTeleport(this._onTeleport.bind(this));
  }

  // --- State getters ---

  get knockbackPulseCount(): number { return this._knockbackPulseCount; }
  get enemiesKnockedBack(): number { return this._enemiesKnockedBack; }

  // ---------------------------------------------------------------------------
  // Teleport handler
  // ---------------------------------------------------------------------------

  private _onTeleport(event: TeleportEvent): void {
    const wells = this.deps.anchorSource.getActiveWells();
    if (wells.length === 0) return;

    const from = { x: event.fromX, y: event.fromY };
    const to   = { x: event.toX,   y: event.toY };

    // Check if either departure or arrival is within any well's pull radius.
    const nearby = wells.some(well => {
      const pullPx = well.pullRadiusTiles * TILE_SIZE;
      const pullSq = pullPx * pullPx;
      return _distSq(from, well.position) <= pullSq ||
             _distSq(to,   well.position) <= pullSq;
    });

    if (!nearby) return;

    // Proximity confirmed — fire arrival knockback pulse.
    this._knockbackPulseCount++;

    const targets = this.deps.enemies.getEnemiesInRadius(to, this.config.arrivalKnockbackRadiusTiles);
    for (const enemyId of targets) {
      this.deps.knockback.pushFromPoint(enemyId, to);
      this._enemiesKnockedBack++;
    }
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  update(_delta: number): void { /* passive — event-driven, nothing to tick */ }
  destroy(): void { /* AstralTeleportSystem has no listener removal API */ }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _distSq(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
